import { levelValues, logMethods, logLevels, timeLevel } from './constants'
import { checkLevel, checkHandler } from './validation'
import createTimer from './timer'

/**
 * Creates a Logger.
 * @method createLogger
 * @param  {Object}   target          The subject of the logs from this logger.
 * @param  {Object}   options
 * @param  {Function} options.handler The function to receive/handle log entries.
 * @param  {string}   options.level   The log level to be set.
 * @return {Logger}
 */
export default function createLogger (target, options = {}) {
  // Validate provided options. Do not let invalid options be set.
  const level = checkLevel(options.level)
  const handler = checkHandler(options.handler)

  /**
   * Logger data.
   * @type {Object}
   * @property {Object}   target  Metadata about the "target" of the Logger.
   * @property {LogLevel} level   The currently set log level.
   * @property {Function} handler The currently set Log Handler.
   */
  const logger = {
    target,
    level,
    handler
  }

  /**
   * Currying function to dynamically create the Logger's logging methods.
   * @method logFunc
   * @param  {string} method   Name of the logger method to create.
   * @param  {string} logLevel The log level for the method.
   * @param  {Object} [injectables] Other values to include in the LogEntry.
   * @return {Function} A log method.
   */
  function logFunc (method, logLevel, injectables) {
    /*
     * Return the function that will be used as `log.<method>`.
     */
    return function (...args) {
      // Compare the logged level and the configured level.
      const shouldLog = levelValues[logLevel] >= levelValues[logger.level]
      // If this entry shouldn't be logged, don't do anything.
      if (!shouldLog) {
        return
      }

      // Create the Log Entry to be handed off to the handler.
      const entry = {
        // Extra data to be added to the LogEntry.
        ...injectables,
        // Meta-info about the log.
        method,
        timestamp: Date.now(),
        level: logLevel,
        target: logger.target,
        // The actual arguments logged.
        messages: [...args]
      }

      logger.handler(entry)
    }
  }

  /**
   * Sets the Logger's current log level.
   * @method setLevel
   * @param  {string} level The logLevel to be set.
   * @throws Throws an error if level is not a valid log level.
   * @example
   * logger.setLevel(logLevels.INFO)
   * logger.info('This will be logged.')
   * logger.debug('This will not be logged.')
   */
  function setLevel (level) {
    logger.level = checkLevel(level)
  }

  /**
   * Gets the Logger's current log level.
   * @method getLevel
   * @return {string} The log level.
   * @example
   * logger.setLevel(logLevels.DEBUG)
   * const level = logger.getLevel()
   * // level === logLevels.DEBUG
   */
  function getLevel () {
    return logger.level
  }

  /**
   * Sets the Logger's current log handler.
   * @method setHandler
   * @param  {Function} handler The log handler to be set.
   * @throws Throws an error if handler is not a function.
   * @example
   * const logger = manager.getLogger('Test', '123')
   * logger.setHandler((logEntry) => {
   *    console.log(logEntry.target.name, ...logEntry.messages)
   * })
   * logger.info('I am a logged message.')
   * // logs: "Test-123 I am a logged message."
   */
  function setHandler (handler) {
    logger.handler = checkHandler(handler)
  }

  /**
   * Gets the Logger's current log handler.
   * @method getHandler
   * @return {Function} The log handler.
   * @example
   * logger.setHandler(customHandler)
   * const handler = logger.getHandler()
   * // handler === customHandler.DEBUG
   */
  function getHandler () {
    return logger.handler
  }

  const api = {
    getHandler,
    setHandler,
    getLevel,
    setLevel,
    get type () {
      return logger.target.type
    },
    get id () {
      return logger.target.id
    },
    get name () {
      return logger.target.name
    }
  }

  // For all supported log methods, create a function on the Logger for it.
  for (const method in logMethods) {
    api[method] = logFunc(method, logMethods[method])
  }

  // Create log functions for the Timer to use.
  // Follows the same style as above logMethods, but curries it once more
  //    to align the timer parameters as needed.
  const timeLog = (message, data) => logFunc(data.event, timeLevel, { timer: data })(message)
  const timeWarn = (message, data) => logFunc('warn', logLevels.WARN, { timer: data })(message)
  const timer = createTimer(timeLog, timeWarn)

  return {
    ...api,
    ...timer
  }
}
